import { useQuery } from '@apollo/react-hooks';
import { AI_ASSISTANT_QUICK_PROMPTS } from 'graphql/aiSearch';

export interface QuickPrompt {
  /** The text on the chat button */
  label: string;
  /** The message the button sends */
  prompt: string;
}

/**
 * What the chat offers until an admin sets their own on the Content Lifecycle
 * page. These are literal prompts: the assistant decides which tools to run, so
 * each one exercises a real capability (search, tagging, SEO metadata,
 * drafting a derived article).
 */
export const BUILT_IN_QUICK_PROMPTS: { article_edit: QuickPrompt[]; default: QuickPrompt[] } = {
  article_edit: [
    { label: 'Find articles related to this one', prompt: 'Find articles related to this one' },
    { label: 'Suggest and add tags', prompt: 'Suggest tags for this article and add them' },
    { label: 'Write an SEO meta description', prompt: 'Write an SEO meta description for this article' },
    { label: 'Summarize as a new article', prompt: 'Summarize this as a new article in a relaxed tone' },
    { label: 'Suggest a sharper headline', prompt: 'Suggest a sharper headline' },
  ],
  default: [
    { label: 'Find articles about a topic', prompt: 'Find articles about a topic' },
    { label: 'What has been published recently?', prompt: 'What has been published recently?' },
    { label: 'Help me discover content', prompt: 'Help me discover content' },
  ],
};

/**
 * The buttons for a page: the ones configured on the Content Lifecycle page,
 * or the built-ins while they load, when none are set, or when the request
 * fails. The welcome state must never render without buttons, so an error is
 * not worth showing here.
 */
export function useQuickPrompts(pageType: string | undefined): QuickPrompt[] {
  const { data } = useQuery(AI_ASSISTANT_QUICK_PROMPTS, {
    variables: { pageType },
    fetchPolicy: 'cache-and-network',
  });
  const configured: QuickPrompt[] = data?.aiAssistantQuickPrompts || [];

  if (configured.length) return configured;
  return pageType === 'article-edit' ? BUILT_IN_QUICK_PROMPTS.article_edit : BUILT_IN_QUICK_PROMPTS.default;
}
